import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    Checkbox,
    FormControlLabel,
    Slider,
    TextField,
    Tooltip,
    RadioGroup,
    FormControl,
    FormLabel,
    Radio,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import api from '../../services/api';
import { Document as AppDocument } from '../../types/Document';
import { useDocument } from '../../contexts/DocumentContext';
import { useHelpContent } from '../../utils/HelpUtils';
import {
  ORIGINAL_FILES_CONTENT_CATEGORY_NAME,
} from '../../utils/CategoryUtils';

const DEFAULT_IMPORT_SUBCASE = 'Imported';

function countContentNodes(node: any): number {
    if (!node) return 0;
    let count = node.type === 'content' ? 1 : 0;
    if (Array.isArray(node.children)) {
        for (const child of node.children) count += countContentNodes(child);
    }
    return count;
}

interface SuggestedTreeDialogProps {
    open: boolean;
    onClose: () => void;
    onRetry: (maxDepth: number, treeStrategy: 'top_down' | 'bottom_up') => Promise<void>;
    suggestedTree: any;
    fileNames: string[];
    loading?: boolean;
    onOpenDocument: (document: AppDocument) => Promise<void>;
    onNewDocument: (name: string, open: boolean) => Promise<AppDocument>;
    error?: string | null;
    initialMaxDepth?: number;
    initialTreeStrategy?: 'top_down' | 'bottom_up';
    importMode?: 'category_tree' | 'content_only';
    setGlobalLoading?: (loading: boolean) => void;
}

const SuggestedTreeDialog: React.FC<SuggestedTreeDialogProps> = (props) => {
    const {
        open,
        onClose,
        onRetry,
        suggestedTree,
        fileNames,
        loading = false,
        onOpenDocument,
        onNewDocument,
        error,
        initialMaxDepth = 1,
        initialTreeStrategy = 'bottom_up',
        importMode = 'category_tree',
        setGlobalLoading,
    } = props;
    const isContentOnly = importMode === 'content_only';
    const { documents, refreshDocuments, refreshDocumentSubcases } = useDocument();
    const { getTooltipTextForState } = useHelpContent();
    const [loadingState, setLoading] = useState(false);
    const [retryLoading, setRetryLoading] = useState(false);
    const [showContent, setShowContent] = useState(true);
    const [maxDepth, setMaxDepth] = useState(initialMaxDepth);
    const [treeStrategy, setTreeStrategy] = useState<'top_down' | 'bottom_up'>(initialTreeStrategy);
    const [subcaseName, setSubcaseName] = useState(DEFAULT_IMPORT_SUBCASE);

    const effectiveSubcaseName = subcaseName.trim() || DEFAULT_IMPORT_SUBCASE;
    const [useSuggestedName, setUseSuggestedName] = useState(false);
    const [customName, setCustomName] = useState('');
    const [mergingOccurred, setMergingOccurred] = useState(false);
    const [nameError, setNameError] = useState<string>(''); // Add validation error state

    const normalizeName = (name: string) =>
        name.replace(/[\u200B-\u200D\uFEFF]/g, '').trim().toLowerCase();

    const getInternalCategoryNames = (): Set<string> => {
        const names = new Set<string>();

        const visit = (node: any, isRoot: boolean) => {
            if (!node) return;
            // Treat all category nodes except the root as "internal"
            if (!isRoot && node.type === 'category' && typeof node.name === 'string' && node.name.trim()) {
                names.add(normalizeName(node.name));
            }
            if (Array.isArray(node.children)) {
                for (const child of node.children) visit(child, false);
            }
        };

        if (suggestedTree?.tree) visit(suggestedTree.tree, true);
        return names;
    };

    const isNameConflictingWithInternalCategory = (name: string): boolean => {
        const normalized = normalizeName(name);
        if (!normalized) return false;
        return getInternalCategoryNames().has(normalized);
    };

    // Validation function for document name
    const validateDocumentName = (name: string): boolean => {
        if (!name.trim()) {
            setNameError('Document name is required');
            return false;
        }

        if (isNameConflictingWithInternalCategory(name)) {
            setNameError('Document name cannot match a category name inside the suggested category structure');
            return false;
        }
        
        // Check if document with this name already exists
        const normalizedName = normalizeName(name);
        const existingDocument = documents.find(doc => 
            doc.label && doc.label.toLowerCase() === normalizedName
        );
        
        if (existingDocument) {
            setNameError('A document with this name already exists');
            return false;
        }
        
        setNameError('');
        return true;
    };

    // Get the final document name based on user choice
    const getFinalDocumentName = (): string => {
        if (isContentOnly) {
            return customName.trim() || 'Untitled';
        }
        if (useSuggestedName && suggestedTree.tree.name) {
            // If using AI name but it conflicts, use custom name instead
            if (isAiNameConflicting() || isAiNameConflictingWithInternalCategory()) {
                return customName.trim() || 'Untitled';
            }
            return suggestedTree.tree.name;
        } else if (customName.trim()) {
            return customName.trim();
        } else if (suggestedTree.tree.name) {
            return suggestedTree.tree.name;
        }
        return 'Untitled';
    };

    // Check if accept buttons should be disabled
    const areAcceptButtonsDisabled = (): boolean => {
        if (loading || retryLoading || loadingState) {
            return true;
        }

        if (isContentOnly) {
            return !customName.trim();
        }
        
        if (useSuggestedName) {
            // If using AI name, check if it conflicts with existing documents
            const aiName = suggestedTree.tree.name;
            if (!aiName) return true;
            
            const normalizedAiName = normalizeName(aiName);
            const existingDocument = documents.find(doc => 
                doc.label && doc.label.toLowerCase() === normalizedAiName
            );
            
            // If AI name conflicts (existing doc OR internal category), require custom name
            if (existingDocument || isNameConflictingWithInternalCategory(aiName)) {
                return !customName.trim();
            }
            
            return false; // AI name is valid
        } else {
            // If using custom name, it must be provided
            return !customName.trim();
        }
    };

    // Check if AI suggested name conflicts with existing documents
    const isAiNameConflicting = (): boolean => {
        if (!useSuggestedName || !suggestedTree.tree.name) return false;
        
        const normalizedAiName = normalizeName(suggestedTree.tree.name);
        const existingDocument = documents.find(doc => 
            doc.label && doc.label.toLowerCase() === normalizedAiName
        );
        
        return !!existingDocument;
    };

    const isAiNameConflictingWithInternalCategory = (): boolean => {
        if (!useSuggestedName || !suggestedTree?.tree?.name) return false;
        return isNameConflictingWithInternalCategory(suggestedTree.tree.name);
    };

    // Check if custom name field should be visible
    const shouldShowCustomNameField = (): boolean => {
        return !useSuggestedName || isAiNameConflicting() || isAiNameConflictingWithInternalCategory();
    };



    // Apply merging when suggestedTree changes
    useEffect(() => {
        if (suggestedTree && suggestedTree.tree) {
            const mergeResult = mergeDuplicateCategories(suggestedTree.tree);
            setMergingOccurred(mergeResult.merged);
            // Note: We don't modify the prop directly, but we could pass this info to parent
        }
    }, [suggestedTree]);

    // Update maxDepth when initialMaxDepth changes
    useEffect(() => {
        setMaxDepth(initialMaxDepth);
    }, [initialMaxDepth]);

    useEffect(() => {
        setTreeStrategy(initialTreeStrategy);
    }, [initialTreeStrategy]);



    // Helper function to merge duplicate categories at the same level
    const mergeDuplicateCategories = (node: any): { node: any, merged: boolean } => {
        if (!node || !node.children || node.children.length === 0) {
            return { node, merged: false };
        }

        // Group children by name (for categories) or type
        const categoryGroups = new Map<string, any[]>();
        const contentNodes: any[] = [];
        let hasMerging = false;

        for (const child of node.children) {
            if (child.type === 'category') {
                const name = child.name || 'Unnamed Category';
                if (!categoryGroups.has(name)) {
                    categoryGroups.set(name, []);
                }
                categoryGroups.get(name)!.push(child);
            } else if (child.type === 'content') {
                contentNodes.push(child);
            }
        }

        // Merge categories with the same name
        const mergedCategories: any[] = [];
        for (const [name, categories] of Array.from(categoryGroups.entries())) {
            if (categories.length === 1) {
                // Single category, just merge its children
                const childResult = mergeDuplicateCategories(categories[0]);
                const mergedCategory = {
                    ...categories[0],
                    children: childResult.node.children || []
                };
                mergedCategories.push(mergedCategory);
                if (childResult.merged) hasMerging = true;
            } else {
                // Multiple categories with the same name, merge them
                console.log(`Merging ${categories.length} categories with name: "${name}"`);
                hasMerging = true;
                
                // Collect all children from all categories with this name
                const allChildren: any[] = [];
                for (const category of categories) {
                    if (category.children && category.children.length > 0) {
                        allChildren.push(...category.children);
                    }
                }

                // Create a single merged category
                const childResult = mergeDuplicateCategories({ children: allChildren });
                const mergedCategory = {
                    name: name,
                    type: 'category',
                    children: childResult.node.children || []
                };
                mergedCategories.push(mergedCategory);
                if (childResult.merged) hasMerging = true;
            }
        }

        // Return the node with merged children
        return {
            node: {
                ...node,
                children: [...mergedCategories, ...contentNodes]
            },
            merged: hasMerging
        };
    };

    // Helper: recursively create categories and content
    const createTree = async (parentId: string, node: any, withContent: boolean, useSuggestedName: boolean = false) => {
        if (!node) return;
        if (node.type === 'category' && Array.isArray(node.children)) {
            // Check if children are all categories, all content, or mixed
            const hasCategory = node.children.some((child: any) => child.type === 'category');
            const hasContent = node.children.some((child: any) => child.type === 'content');
            
            if (hasCategory && hasContent) {
                // Mixed children - this violates the constraint
                // Create categories first, then create a special category for unplaced content
                console.log('⚠️ Mixed children detected - creating special category for unplaced content');
                
                // First, create all category children
                for (const child of node.children) {
                    if (child.type === 'category') {
                        const categoryRes = await api.post('/categories', {
                            parentId: parentId || '',
                            name: child.name,
                            description: ''
                        });
                        const newCategory = categoryRes.data;
                        await createTree(newCategory.id, child, withContent, false);
                    }
                }
                
                // Then, create a special category for unplaced content
                if (withContent) {
                    const uncategorizedCategoryRes = await api.post('/categories', {
                        parentId: parentId || '',
                        name: 'Uncategorized Content',
                        description: 'Content that could not be placed in specific categories due to constraint violations'
                    });
                    const uncategorizedCategory = uncategorizedCategoryRes.data;
                    
                    // Place all content in this special category
                    for (const child of node.children) {
                        if (child.type === 'content') {
                            await createContentInCategory(uncategorizedCategory.id, child);
                        }
                    }
                }
            } else if (hasCategory) {
                // Only categories - process normally
                for (const child of node.children) {
                    if (child.type === 'category') {
                        const categoryRes = await api.post('/categories', {
                            parentId: parentId || '',
                            name: child.name,
                            description: ''
                        });
                        const newCategory = categoryRes.data;
                        await createTree(newCategory.id, child, withContent, false);
                    }
                }
            } else if (hasContent && withContent) {
                // Only content - process normally
                for (const child of node.children) {
                    if (child.type === 'content') {
                        await createContentInCategory(parentId, child);
                    }
                }
            }
        }
    };

    // Helper function to create content in a category
    const createContentInCategory = async (parentId: string, child: any) => {
                        // Handle content nodes with text property (from hierarchical clustering)
                        if (child.text) {
                            await api.post('/content', {
                                parentId: parentId || '',
                                text: child.text,
                                subcase: effectiveSubcaseName,
                                id: '',
                                skipDuplicateCheck: true,
                            });
                        }
                        // Handle content nodes with index property (from content-based clustering)
                        else if (child.index !== undefined && suggestedTree.originalContents && suggestedTree.originalContents[child.index]) {
                            const originalContent = suggestedTree.originalContents[child.index];
                            await api.post('/content', {
                                parentId: parentId || '',
                                text: originalContent.text || `Content ${child.index + 1}`,
                                subcase: originalContent.subcase || effectiveSubcaseName,
                                id: '',
                                skipDuplicateCheck: true,
                            });
        }
    };

    const handleAcceptWithContent = async () => {
        try {
            const documentName = getFinalDocumentName();
            
            // Validate document name
            if (!validateDocumentName(documentName)) {
                return;
            }
            
            setLoading(true);
            setGlobalLoading?.(true);
            
            // Create document with the suggested tree and content
            const document: AppDocument = await onNewDocument(documentName, false);
            
            // Recursively create the tree structure and content
            await createTree(document.categoryId || '', suggestedTree.tree, true, useSuggestedName);
            await refreshDocuments();
            const subcases = document.categoryId
              ? await refreshDocumentSubcases(document.categoryId)
              : [];
            await onOpenDocument({ ...document, subcases });
            onClose();
        } catch (err) {
            console.error('Error creating document:', err);
        } finally {
            // Clear spinner only after create + full document open complete.
            setLoading(false);
            setGlobalLoading?.(false);
        }
    };

    const handleAcceptTreeOnly = async () => {
        try {
            const documentName = getFinalDocumentName();
            
            // Validate document name
            if (!validateDocumentName(documentName)) {
                return;
            }
            
            setLoading(true);
            setGlobalLoading?.(true);
            
            // Create document with just the tree structure
            const document: AppDocument = await onNewDocument(documentName, false);
            
            // Create a temporary category to store original file content (same pattern as organize-into-subcategories flow)
            const tempCategoryRes = await api.post('/categories', {
                parentId: document.categoryId || '',
                name: ORIGINAL_FILES_CONTENT_CATEGORY_NAME,
                description: 'Temporary category containing original file content'
            });
            const tempCategory = tempCategoryRes.data;
            
            // Extract and store original file content from the tree structure
            const extractContentFromTree = (node: any): any[] => {
                const contents: any[] = [];
                
                if (node.type === 'content') {
                    // Handle content nodes with text property (from hierarchical clustering)
                    if (node.text) {
                        contents.push({
                            text: node.text,
                            subcase: effectiveSubcaseName,
                        });
                    }
                    // Handle content nodes with index property (from content-based clustering)
                    else if (node.index !== undefined && suggestedTree.originalContents && suggestedTree.originalContents[node.index]) {
                        const originalContent = suggestedTree.originalContents[node.index];
                        contents.push({
                            text: originalContent.text || `Content ${node.index + 1}`,
                            subcase: originalContent.subcase || effectiveSubcaseName,
                        });
                    }
                } else if (node.children && Array.isArray(node.children)) {
                    // Recursively extract content from children
                    for (const child of node.children) {
                        contents.push(...extractContentFromTree(child));
                    }
                }
                
                return contents;
            };
            
            // Extract all content from the tree
            const extractedContents = extractContentFromTree(suggestedTree.tree);
            
            // Store extracted content in the temporary category
            if (extractedContents.length > 0) {
                for (const content of extractedContents) {
                    try {
                        await api.post('/content', {
                            parentId: tempCategory.id,
                            text: content.text,
                            subcase: content.subcase,
                            id: '',
                            skipDuplicateCheck: true,
                        });
                    } catch (err) {
                        console.error('Failed to store extracted content:', err);
                    }
                }
            }
            
            // Create the tree structure without content
            await createTree(document.categoryId || '', suggestedTree.tree, false, useSuggestedName);
            await refreshDocuments();
            const subcases = document.categoryId
              ? await refreshDocumentSubcases(document.categoryId)
              : [];
            await onOpenDocument({ ...document, subcases });
            onClose();
        } catch (err) {
            console.error('Error creating document:', err);
        } finally {
            setLoading(false);
            setGlobalLoading?.(false);
        }
    };

    /** Folders only — no leaf content and no Original Files Content stash. */
    const handleAcceptCategoriesOnly = async () => {
        try {
            const documentName = getFinalDocumentName();
            if (!validateDocumentName(documentName)) {
                return;
            }

            setLoading(true);
            setGlobalLoading?.(true);

            const document: AppDocument = await onNewDocument(documentName, false);
            await createTree(document.categoryId || '', suggestedTree.tree, false, useSuggestedName);
            await refreshDocuments();
            const subcases = document.categoryId
              ? await refreshDocumentSubcases(document.categoryId)
              : [];
            await onOpenDocument({ ...document, subcases });
            onClose();
        } catch (err) {
            console.error('Error creating document:', err);
        } finally {
            setLoading(false);
            setGlobalLoading?.(false);
        }
    };

    const handleRetry = async () => {
        try {
            setRetryLoading(true);
            await onRetry(maxDepth, treeStrategy);
        } catch (err) {
            console.error('Error getting a new suggestion:', err);
        } finally {
            setRetryLoading(false);
        }
    };

    const handleClose = () => {
        setShowContent(false);
        setMaxDepth(3);
        setTreeStrategy('bottom_up');
        setSubcaseName(DEFAULT_IMPORT_SUBCASE);
        setUseSuggestedName(false);
        setCustomName('');
        onClose();
    };

    // Helper to detect RTL (Hebrew/Arabic)
    const isRTL = suggestedTree && suggestedTree.tree && /[\u0590-\u05FF\u0600-\u06FF]/.test(suggestedTree.tree.name);

    // Helper function to detect content categories
    const isContentCategory = (node: any): boolean => {
        if (!node.children || node.children.length === 0) {
            return true; // No children = content category
        }
        // Has children of type 'content' = content category
        return node.children.some((child: any) => child.type === 'content');
    };

    const renderContentNode = (node: any, path: string, level: number): React.ReactNode[] => {
        if (!node) return [];
        const currentPath = path + (node.text || node.name || '');
        const padding = isRTL ? { pr: level * 2 } : { pl: level * 2 };

        if (node.type === 'category' && Array.isArray(node.children)) {
            return node.children.flatMap((child: any, idx: number) =>
                renderContentNode(child, `${currentPath}-${idx}`, level)
            );
        }

        if (showContent && node.type === 'content' && node.text) {
            return [
                <Box key={currentPath} sx={{ display: 'flex', alignItems: 'flex-start', ...padding, mb: 0.5 }} dir={isRTL ? 'rtl' : 'ltr'}>
                    <DescriptionIcon sx={{ color: '#ff9800', mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0, fontSize: 20, mt: 0.5 }} />
                    <Typography variant="body2" sx={{ fontStyle: 'italic', wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.5, textAlign: isRTL ? 'right' : 'left', flex: 1 }} dir="auto">
                        {node.text}
                    </Typography>
                </Box>
            ];
        }

        if (showContent && node.type === 'content' && node.index !== undefined) {
            let contentText = `Content ${node.index + 1}`;
            let subcaseText = '';

            if (suggestedTree.originalContents && suggestedTree.originalContents[node.index]) {
                const originalContent = suggestedTree.originalContents[node.index];
                contentText = originalContent.text || contentText;
                subcaseText = originalContent.subcase || '';
            }

            return [
                <Box key={currentPath} sx={{ display: 'flex', flexDirection: 'column', ...padding, mb: 0.5 }} dir={isRTL ? 'rtl' : 'ltr'}>
                    {subcaseText && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <DescriptionIcon sx={{ color: '#ff9800', mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0, fontSize: 18, mt: 0.5 }} />
                            <Typography variant="body2" sx={{ color: '#333', fontWeight: 600, wordBreak: 'break-word', textAlign: isRTL ? 'right' : 'left' }} dir="auto">
                                {subcaseText}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Typography variant="body2" sx={{ fontStyle: 'italic', wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.5, textAlign: isRTL ? 'right' : 'left', flex: 1 }} dir="auto">
                            {contentText}
                        </Typography>
                    </Box>
                </Box>
            ];
        }

        return [];
    };

    const renderContentOnlyPreview = (): React.ReactNode[] => {
        const children = suggestedTree?.tree?.children;
        if (!Array.isArray(children)) return [];
        return children.flatMap((child: any, idx: number) => renderContentNode(child, `content-${idx}`, 0));
    };

    const renderTree = (node: any, path: string = '', level: number = 0): React.ReactNode[] => {
        if (!node) return [];
        const currentPath = path + (node.name || node.text || '');
        const padding = isRTL ? { pr: level * 2 } : { pl: level * 2 };

        // Category node (including root)
        if (node.type === 'category' && node.name) {
            const children = Array.isArray(node.children)
                ? node.children.flatMap((child: any, idx: number) => renderTree(child, currentPath + '-' + idx, level + 1))
                : [];
            const isContentCat = isContentCategory(node);
            return [
                <Box key={currentPath} sx={{ display: 'flex', alignItems: 'center', ...padding, mb: 0.5 }} dir={isRTL ? 'rtl' : 'ltr'}>
                    {isContentCat ? (
                        <DescriptionIcon sx={{ color: '#87CEEB', mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0 }} />
                    ) : (
                        <FolderIcon sx={{ color: '#1976d2', mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0 }} />
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 700, wordBreak: 'break-word', textAlign: isRTL ? 'right' : 'left', flex: 1 }} dir="auto">
                        {node.name}
                    </Typography>
                </Box>,
                ...children
            ];
        }

        // Content node with text property (from hierarchical clustering)
        if (showContent && node.type === 'content' && node.text) {
            return [
                <Box key={currentPath} sx={{ display: 'flex', alignItems: 'flex-start', ...padding, mb: 0.5 }} dir={isRTL ? 'rtl' : 'ltr'}>
                    <DescriptionIcon sx={{ color: '#ff9800', mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0, fontSize: 20, mt: 0.5 }} />
                    <Typography variant="body2" sx={{ fontStyle: 'italic', wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.5, textAlign: isRTL ? 'right' : 'left', flex: 1 }} dir="auto">
                        {node.text}
                    </Typography>
                </Box>
            ];
        }

        // Content node with index property (from content-based clustering)
        if (showContent && node.type === 'content' && node.index !== undefined) {
            // Try to get the original content if available
            let contentText = `Content ${node.index + 1}`;
            let subcaseText = '';
            
            if (suggestedTree.originalContents && suggestedTree.originalContents[node.index]) {
                const originalContent = suggestedTree.originalContents[node.index];
                contentText = originalContent.text || contentText;
                subcaseText = originalContent.subcase || '';
            }

            return [
                <Box key={currentPath} sx={{ display: 'flex', flexDirection: 'column', ...padding, mb: 0.5 }} dir={isRTL ? 'rtl' : 'ltr'}>
                    {subcaseText && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <DescriptionIcon sx={{ color: '#ff9800', mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0, fontSize: 18, mt: 0.5 }} />
                            <Typography variant="body2" sx={{ color: '#333', fontWeight: 600, wordBreak: 'break-word', textAlign: isRTL ? 'right' : 'left' }} dir="auto">
                                {subcaseText}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Typography variant="body2" sx={{ fontStyle: 'italic', wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.5, textAlign: isRTL ? 'right' : 'left', flex: 1 }} dir="auto">
                            {contentText}
                        </Typography>
                    </Box>
                </Box>
            ];
        }

        return [];
    };

    // Fallback: render as a list if tree is empty
    const renderFallbackList = (node: any) => {
        if (!node) return null;
        if (node.type === 'category' && node.name) {
            return (
                <li key={node.name}>
                    <b>{node.name}</b>
                    <ul>
                        {Array.isArray(node.children)
                            ? node.children.map((child: any, idx: number) => renderFallbackList(child))
                            : null}
                    </ul>
                </li>
            );
        }
        if (node.type === 'content' && node.text) {
            return <li key={node.text}><i>{node.text}</i></li>;
        }
        return null;
    };

    // Log the tree data
    console.log('suggestedTree.tree:', suggestedTree && suggestedTree.tree);

    return (
        <Dialog
            open={open}
            onClose={(_event, reason) => {
                if (reason === 'backdropClick') return;
                handleClose();
            }}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>{isContentOnly ? 'Content preview' : 'New document preview'}</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!isContentOnly && mergingOccurred && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Some categories with duplicate names were automatically merged to maintain a valid structure.
                    </Alert>
                )}

                <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Files: <b>{fileNames.join(', ')}</b>
                    </Typography>
                    {!isContentOnly && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <FormControl component="fieldset" variant="standard">
                            <FormLabel component="legend">
                                <Tooltip title={getTooltipTextForState('suggest-tree-strategy', true)} placement="top">
                                    <span>Building strategy</span>
                                </Tooltip>
                            </FormLabel>
                            <RadioGroup
                                row
                                value={treeStrategy}
                                onChange={(e) => setTreeStrategy(e.target.value as 'top_down' | 'bottom_up')}
                            >
                                <FormControlLabel value="top_down" control={<Radio />} label="Top-down" />
                                <FormControlLabel value="bottom_up" control={<Radio />} label="Bottom-up (default)" />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                    )}
                    {!isContentOnly && (
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Typography id="max-depth-slider" gutterBottom>
                            Maximum tree depth: {maxDepth}
                        </Typography>
                        <Tooltip title={getTooltipTextForState('suggest-tree-max-depth', true)} placement="top">
                            <Slider
                                value={maxDepth}
                                onChange={(_, value) => setMaxDepth(value as number)}
                                aria-labelledby="max-depth-slider"
                                valueLabelDisplay="auto"
                                step={1}
                                marks
                                min={1}
                                max={5}
                            />
                        </Tooltip>
                    </Box>
                    )}
                    <Tooltip title={getTooltipTextForState('suggest-tree-subcase-name', true)} placement="top">
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                label="Subcase name"
                                value={subcaseName}
                                onChange={(e) => setSubcaseName(e.target.value)}
                                size="small"
                                fullWidth
                                placeholder="e.g. Imported, Chapter 1"
                            />
                        </Box>
                    </Tooltip>
                    {!isContentOnly && (
                    <Tooltip title={getTooltipTextForState('suggest-tree-use-suggested-name', true)} placement="top">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useSuggestedName}
                                    onChange={(e) => {
                                        setUseSuggestedName(e.target.checked);
                                        if (e.target.checked && nameError) {
                                            setNameError('');
                                        }
                                    }}
                                />
                            }
                            label={`Use AI-suggested document name: "${suggestedTree?.tree?.name ?? ''}"`}
                            sx={{ mt: 2 }}
                        />
                    </Tooltip>
                    )}
                    {!isContentOnly && useSuggestedName && !loadingState && !retryLoading && !loading && isAiNameConflicting() && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            The AI-suggested name "{suggestedTree.tree.name}" conflicts with an existing document. Please provide a different name below.
                        </Alert>
                    )}
                    {!isContentOnly && useSuggestedName && !loadingState && !retryLoading && !loading && isAiNameConflictingWithInternalCategory() && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            The AI-suggested name "{suggestedTree.tree.name}" matches a category name inside the suggested category structure. Please provide a different document name below.
                        </Alert>
                    )}
                    
                    {(isContentOnly || shouldShowCustomNameField()) && (
                        <Tooltip title={getTooltipTextForState('suggest-tree-custom-name', true)} placement="top">
                            <TextField
                                fullWidth
                                label={isContentOnly ? 'Document Name' : (
                                    useSuggestedName && (isAiNameConflicting() || isAiNameConflictingWithInternalCategory())
                                        ? "Override AI Name (Required)"
                                        : "Custom Document Name"
                                )}
                                value={customName}
                                onChange={(e) => {
                                    setCustomName(e.target.value);
                                    if (nameError) {
                                        setNameError('');
                                    }
                                }}
                                placeholder={isContentOnly ? 'Enter a name for the new document' : (
                                    useSuggestedName && (isAiNameConflicting() || isAiNameConflictingWithInternalCategory())
                                        ? "Enter a name to override the AI name"
                                        : "Enter a custom name for the document"
                                )}
                                margin="normal"
                                sx={{ mt: 2 }}
                                required={isContentOnly}
                                error={!!nameError}
                                helperText={
                                    nameError ||
                                    (useSuggestedName && (isAiNameConflicting() || isAiNameConflictingWithInternalCategory())
                                        ? 'AI name is not usable. Enter a different document name.'
                                        : undefined)
                                }
                            />
                        </Tooltip>
                    )}
                    <Tooltip title={getTooltipTextForState('suggest-tree-show-content', true)} placement="top">
                        <FormControlLabel
                            control={<Checkbox checked={showContent} onChange={e => setShowContent(e.target.checked)} />}
                            label="Show content text"
                            sx={{ mt: 2 }}
                        />
                    </Tooltip>
                </Box>
                {suggestedTree?.tree && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" color="text.primary">
                            Preview
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {typeof suggestedTree.segmentCount === 'number'
                                ? `${suggestedTree.segmentCount} text segment${suggestedTree.segmentCount === 1 ? '' : 's'} · `
                                : ''}
                            {countContentNodes(suggestedTree.tree)} content{countContentNodes(suggestedTree.tree) === 1 ? '' : 's'} created
                        </Typography>
                    </Box>
                )}
                <Box sx={{ position: 'relative', height: 400, overflow: 'auto', border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1, p: 2 }}>
                    {!suggestedTree ? (
                        <Typography color="error">No category structure data available</Typography>
                    ) : !suggestedTree.tree ? (
                        <Typography color="error">Invalid category structure</Typography>
                    ) : (
                        <Box>
                            {isContentOnly ? renderContentOnlyPreview() : renderTree(suggestedTree.tree)}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Tooltip title={getTooltipTextForState('cancel', true)} placement="top">
                    <Button onClick={handleClose} disabled={loading || retryLoading || loadingState}>
                        Cancel
                    </Button>
                </Tooltip>
                {!isContentOnly && (
                <Tooltip title={getTooltipTextForState('suggest-tree-retry', true)} placement="top">
                    <Button onClick={handleRetry} disabled={loading || retryLoading || loadingState} variant="outlined">
                        Retry
                    </Button>
                </Tooltip>
                )}
                {!isContentOnly && (
                <Tooltip title={getTooltipTextForState('suggest-tree-accept-categories-only', true)} placement="top">
                    <Button
                        onClick={handleAcceptCategoriesOnly}
                        variant="outlined"
                        disabled={areAcceptButtonsDisabled()}
                    >
                        Structure only
                    </Button>
                </Tooltip>
                )}
                {!isContentOnly && (
                <Tooltip title={getTooltipTextForState('suggest-tree-accept-tree-only', true)} placement="top">
                    <Button
                        onClick={handleAcceptTreeOnly}
                        variant="outlined"
                        disabled={areAcceptButtonsDisabled()}
                    >
                        Keep content separate
                    </Button>
                </Tooltip>
                )}
                <Tooltip title={getTooltipTextForState('suggest-tree-accept-with-content', true)} placement="top">
                    <Button
                        onClick={handleAcceptWithContent}
                        variant="outlined"
                        disabled={areAcceptButtonsDisabled()}
                    >
                        {isContentOnly ? 'Create document' : 'Content in folders'}
                    </Button>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

export default SuggestedTreeDialog;